import { createSelector } from "reselect"

// selectors
import { dataSourceHasData, selectWidgetDataSources } from "app/static/frontend/widgets/selectors/selectors"

// helpers
import { formatNumberForDisplay } from "shared/imports/sharedHelperFunctions"

const selectMeta = (_, props) => props.widget && props.widget.meta

export const selectOperation = (_, props) => props.widget && props.widget.operation

export const selectIsFraction = createSelector(
    selectOperation,
    (operation) => operation === DjangIO.app.widgets.types.WidgetOperation.fraction.value,
)

export const selectLabels = createSelector(
    selectMeta,
    selectIsFraction,
    (meta, isFraction) =>
        meta &&
        (isFraction && meta.labels[0] && meta.labels[1] ? [`${meta.labels[0]} / ${meta.labels[1]}`] : meta.labels),
)

export const getColor = ({ colors, data, isFraction }) => {
    if (colors && ((isFraction && data && data.length) || data)) {
        const normalizedData = isFraction ? data[0] : data

        const color = Object
            // get array of arrays of [key,value] pairs
            // {25: "#FF00FF", 0: "#0000FF"} to
            // [[0, "#0000FF"], [25, "#FF00FF"]]
            .entries(colors)
            // reverse it so the largest keys are at the beginning
            // [[25, "#FF00FF"], [0, "#0000FF"]]
            .reverse()
            // find the first boundary that the normalizedData is greater than
            // (if normalizedData is 15, we want #0000FF)
            // (if normalizedData is 28, we want #FF00FF)
            .find(([colorKey, colorValue]) =>
                // Because Javascript converts all Object keys to strings
                // (even if they are initially passed as integers),
                // we need to manually convert the string colorKey back to its integer equivalent
                // to properly compare the two values
                parseInt(normalizedData) >= parseInt(colorKey) ? colorValue : undefined,
            )

        return color && color[1]
    }

    return undefined
}

export const getData = ({ data, formatData, isTransactionSum, operation }) => {
    if (!data && data !== 0) {
        return data
    }

    if (data === "N/A") {
        return data
    }

    if (isTransactionSum) {
        const formatter = new Intl.NumberFormat(
            // "Use undefined in place of the first argument ('en-US' in the example)
            // to use the system locale (the user locale in case the code is running in a browser)."
            // https://stackoverflow.com/a/16233919/6201291
            // TODO: use "en-US" locale if we want to force US number format
            undefined,
            {
                style: "currency",
                currency: "USD",
            },
        )

        return formatter.format(data)
    }

    if (typeof data === "object") {
        return [
            getData({
                data: data[0],
                formatData,
            }),
            getData({
                data: data[1],
                formatData,
            }),
        ]
    }

    const percentageData = Number((data * 100).toFixed(2))
    const defaultDataNumber = Number(data)
    const defaultData =
        // iff decimal
        defaultDataNumber % 1 !== 0
            ? // truncate to two decimal places
              defaultDataNumber.toFixed(2)
            : // if not, just convert to Number() as usual
              defaultDataNumber

    switch (operation) {
        case DjangIO.app.widgets.types.WidgetOperation.percentage.value:
            return formatData ? `${percentageData}%` : `${percentageData}`
        default:
            return formatData ? formatNumberForDisplay(defaultData) : `${defaultData}`
    }
}

export const selectNumberWidgetCanLoad = createSelector(
    selectWidgetDataSources,
    selectOperation,
    (dataSources, operation) => {
        if (!dataSources.length) {
            return false
        }

        if (!operation || DjangIO.app.widgets.types.WidgetOperation.by_value(operation).number_of_data_sources === 1) {
            return dataSourceHasData(dataSources[0])
        }

        return dataSourceHasData(dataSources[0]) && dataSourceHasData(dataSources[1])
    },
)

export const selectSegueArgs = createSelector(selectWidgetDataSources, (dataSources) => {
    if (!dataSources.length) {
        return []
    }

    return dataSources.map((each) => ({
        quorumDataType: each.safed_search.data_type,
        initialFilter: {
            id__in: each.safed_search.added_ids,
            deleted_ids: each.safed_search.deleted_ids,
            ...((each.safed_search.filters && each.safed_search.filters[0]) || {}),
            // Add Dashboard global date filters
            ...(each.additional_filters || {}),
        },
    }))
})

export const selectIsTransactionSum = createSelector(selectWidgetDataSources, (dataSources) =>
    !dataSources.length
        ? undefined
        : dataSources.some(
              (dataSource) =>
                  dataSource.group_by &&
                  dataSource.group_by.aggregation_field &&
                  dataSource.safed_search &&
                  [
                      DjangIO.app.models.QuorumDataType.transaction.value,
                      DjangIO.app.models.QuorumDataType.pacc_pac_transaction.value,
                  ].includes(dataSource.safed_search.data_type),
          ),
)
